import {type Insets, Platform} from 'react-native'
import {type AppBskyActorDefs, BSKY_LABELER_DID} from '@atproto/api'

import {type ProxyHeaderValue} from '#/state/session/agent'
import {BLUESKY_PROXY_DID, CHAT_PROXY_DID, IS_DEV} from '#/env'

export const LOCAL_DEV_SERVICE =
  Platform.OS === 'android' ? 'http://10.0.2.2:2583' : 'http://localhost:2583'
export const STAGING_SERVICE = 'https://staging.bsky.dev'
export const BSKY_SERVICE =
  process.env.EXPO_PUBLIC_BSKY_SERVICE || 'https://pds.taderok.com'
export const BSKY_SERVICE_DID =
  process.env.EXPO_PUBLIC_BSKY_SERVICE_DID || 'did:web:pds.taderok.com'
export const PUBLIC_BSKY_SERVICE = 'https://appview.taderok.com'
export const OGCARD_HOST =
  process.env.EXPO_PUBLIC_OGCARD_HOST || 'https://appview.taderok.com'
export const SHORT_LINK_HOST =
  process.env.EXPO_PUBLIC_SHORT_LINK_HOST || 'https://go.taderok.com'
export const DEFAULT_SERVICE = BSKY_SERVICE
const HELP_DESK_LANG = 'en-us'
export const HELP_DESK_URL = `https://blueskyweb.zendesk.com/hc/${HELP_DESK_LANG}`
export const EMBED_SERVICE = 'https://embed.bsky.app'
export const EMBED_SCRIPT = `${EMBED_SERVICE}/static/embed.js`
export const BSKY_DOWNLOAD_URL = 'https://bsky.app/download'
export const STARTER_PACK_MAX_SIZE = 150
export const CARD_ASPECT_RATIO = 1200 / 630

// HACK
// Yes, this is exactly what it looks like. It's a hard-coded constant
// reflecting the number of new users in the last week. We don't have
// time to add a route to the servers for this so we're just going to hard
// code and update this number with each release until we can get the
// server route done.
// -prf
export const JOINED_THIS_WEEK = 560000 // estimate as of 12/18/24

export const ENABLE_APP_CONFIG_WORKER = false
export const ENABLE_LIVE_EVENTS_WORKER = false

export const DISCOVER_DEBUG_DIDS: Record<string, true> = {
  'did:plc:oisofpd7lj26yvgiivf3lxsi': true, // hailey.at
  'did:plc:p2cp5gopk7mgjegy6wadk3ep': true, // samuel.bsky.team
  'did:plc:ragtjsm2j2vknwkz3zp4oxrd': true, // pfrazee.com
  'did:plc:vpkhqolt662uhesyj6nxm7ys': true, // why.bsky.team
  'did:plc:3jpt2mvvsumj2r7eqk4gzzjz': true, // esb.lol
  'did:plc:vjug55kidv6sye7ykr5faxxn': true, // emilyliu.me
  'did:plc:tgqseeot47ymot4zro244fj3': true, // iwsmith.bsky.social
  'did:plc:2dzyut5lxna5ljiaasgeuffz': true, // darrin.bsky.team
}

const BASE_FEEDBACK_FORM_URL = `${HELP_DESK_URL}/requests/new`
export function FEEDBACK_FORM_URL({
  email,
  handle,
}: {
  email?: string
  handle?: string
}): string {
  let str = BASE_FEEDBACK_FORM_URL
  if (email) {
    str += `?tf_anonymous_requester_email=${encodeURIComponent(email)}`
    if (handle) {
      str += `&tf_17205412673421=${encodeURIComponent(handle)}`
    }
  }
  return str
}

export const MAX_DISPLAY_NAME = 64
export const MAX_DESCRIPTION = 256

export const MAX_GRAPHEME_LENGTH = 300

export const MAX_DRAFT_GRAPHEME_LENGTH = 1000

export const MAX_DM_GRAPHEME_LENGTH = 1000

// Recommended is 100 per: https://www.w3.org/WAI/GL/WCAG20/tests/test3.html
// but increasing limit per user feedback
export const MAX_ALT_TEXT = 2000

export const MAX_REPORT_REASON_GRAPHEME_LENGTH = 2000

export function IS_TEST_USER(handle?: string) {
  return handle && handle?.endsWith('.test')
}

export function IS_PROD_SERVICE(url?: string) {
  return url && url !== STAGING_SERVICE && !url.startsWith(LOCAL_DEV_SERVICE)
}

export const PROD_DEFAULT_FEED = (rkey: string) =>
  `at://did:plc:ytstyrxhluttsawzuyf2yfg4/app.bsky.feed.generator/${rkey}`

export const STAGING_DEFAULT_FEED = (rkey: string) =>
  `at://did:plc:yofh3kx63drvfljkibw5zuxo/app.bsky.feed.generator/${rkey}`

export const PROD_FEEDS = [
  `feedgen|${PROD_DEFAULT_FEED('trending')}`,
  `feedgen|${PROD_DEFAULT_FEED('trending')}`,
]

export const STAGING_FEEDS = [
  `feedgen|${STAGING_DEFAULT_FEED('trending')}`,
  `feedgen|${STAGING_DEFAULT_FEED('trending')}`,
]

export const POST_IMG_MAX = {
  width: 2000,
  height: 2000,
  size: 1000000,
}

export const STAGING_LINK_META_PROXY =
  'https://cardyb.staging.bsky.dev/v1/extract?url='

export const PROD_LINK_META_PROXY = 'https://cardyb.bsky.app/v1/extract?url='

export function LINK_META_PROXY(_serviceUrl: string) {
  if (IS_DEV) {
    return STAGING_LINK_META_PROXY
  }

  return PROD_LINK_META_PROXY
}

export const STATUS_PAGE_URL = 'https://status.bsky.app/'

// Hitslop constants
export const createHitslop = (size: number): Insets => ({
  top: size,
  left: size,
  bottom: size,
  right: size,
})
export const HITSLOP_10 = createHitslop(10)
export const HITSLOP_20 = createHitslop(20)
export const HITSLOP_30 = createHitslop(30)
export const LANG_DROPDOWN_HITSLOP = {top: 10, bottom: 10, left: 4, right: 4}
export const BACK_HITSLOP = HITSLOP_30
export const MAX_POST_LINES = 25

export const BSKY_APP_ACCOUNT_DID = 'did:plc:ytstyrxhluttsawzuyf2yfg4'

export const BSKY_FEED_OWNER_DIDS = [
  BSKY_APP_ACCOUNT_DID,
  'did:plc:vpkhqolt662uhesyj6nxm7ys',
  'did:plc:q6gjnaw2blty4crticxkmujt',
]

/*
 * The ranked For You feed, as its own generator record.
 *
 * It used to be an alias for the `trending` generator, so "Trending" and
 * "For You" were two names for one AT-URI. The ranked feed now has a record of
 * its own: `trending` stays what the feed generator publishes, and `for-you` is
 * what the AppView ranks. The AppView's `BSKY_FOR_YOU_FEED_URIS` must name this
 * same record.
 *
 * `EXPO_PUBLIC_FOR_YOU_FEED_URI` overrides this side alone, for a build pointed
 * at a deployment that does not serve `for-you` yet (set it to the `trending`
 * record there). Unset is the normal case.
 */
const configuredForYouFeedUri = process.env.EXPO_PUBLIC_FOR_YOU_FEED_URI
export const FOR_YOU_FEED_URI =
  configuredForYouFeedUri && configuredForYouFeedUri.length > 0
    ? configuredForYouFeedUri
    : 'at://did:plc:ytstyrxhluttsawzuyf2yfg4/app.bsky.feed.generator/for-you'
export const DISCOVER_FEED_URI = FOR_YOU_FEED_URI
/** The feed generator's own Trending feed, no longer the ranked one. */
export const TRENDING_FEED_URI =
  'at://did:plc:ytstyrxhluttsawzuyf2yfg4/app.bsky.feed.generator/trending'
export const VIDEO_FEED_URI =
  'at://did:plc:ytstyrxhluttsawzuyf2yfg4/app.bsky.feed.generator/videos'
export const STAGING_VIDEO_FEED_URI =
  'at://did:plc:yofh3kx63drvfljkibw5zuxo/app.bsky.feed.generator/videos'
export const VIDEO_FEED_URIS = [VIDEO_FEED_URI, STAGING_VIDEO_FEED_URI]
export const CAPSULE_FEED_URI =
  'at://did:plc:ytstyrxhluttsawzuyf2yfg4/app.bsky.feed.generator/capsules'
export const CAPSULE_FEED_URIS = [CAPSULE_FEED_URI]

// Feature flags
export const ENABLE_INVITE_LINKS = true
export const ENABLE_GROUP_CHATS = true

export const FEATURE_VIDEO_UPLOAD = true // video post upload + video feed + video grid in feeds
export const FEATURE_CAPSULE_POST = true // capsule creation + capsule feed + capsule grid in feeds
export const FEATURE_FLASHBOOK_POST = true // flashbook creation + display in feeds
export const FEATURE_DASHBOARD_MENU = true // Dashboard nav item (desktop LeftNav)
export const FEATURE_WALLET_MENU = true // Wallet nav item (desktop LeftNav + mobile Drawer)
export const FEATURE_HUB_MENU = true // Hub nav item (desktop LeftNav + mobile Drawer)
export const FEATURE_STARTERPACK = true // Entourage/StarterPack tab on profiles + creation
export const FEATURE_FAMILY_LINK = true // Family link entry in Account Settings

export const DISCOVER_SAVED_FEED = {
  type: 'feed',
  value: DISCOVER_FEED_URI,
  pinned: true,
}
export const TIMELINE_SAVED_FEED = {
  type: 'timeline',
  value: 'following',
  pinned: true,
}
export const VIDEO_SAVED_FEED = {
  type: 'feed',
  value: VIDEO_FEED_URI,
  pinned: true,
}
export const CAPSULE_SAVED_FEED = {
  type: 'feed',
  value: CAPSULE_FEED_URI,
  pinned: true,
}

export const RECOMMENDED_SAVED_FEEDS: Pick<
  AppBskyActorDefs.SavedFeed,
  'type' | 'value' | 'pinned'
>[] = [
  DISCOVER_SAVED_FEED,
  TIMELINE_SAVED_FEED,
  ...(FEATURE_CAPSULE_POST ? [CAPSULE_SAVED_FEED] : []),
]

export const KNOWN_SHUTDOWN_FEEDS = [
  'at://did:plc:wqowuobffl66jv3kpsvo7ak4/app.bsky.feed.generator/the-algorithm', // for you by skygaze
]

export const GIF_SERVICE = 'https://gifs.bsky.app'

export const GIF_KLIPY_SEARCH = (params: string) =>
  `${GIF_SERVICE}/klipy/v2/search?${params}`
export const GIF_KLIPY_FEATURED = (params: string) =>
  `${GIF_SERVICE}/klipy/v2/featured?${params}`

export const MAX_LABELERS = 20

export const VIDEO_SERVICE = BSKY_SERVICE
export const VIDEO_SERVICE_DID = BSKY_SERVICE_DID

export const VIDEO_MAX_DURATION_MS = 3 * 60 * 1000 // 3 minutes in milliseconds
/**
 * Maximum size of a video in megabytes, _not_ mebibytes. Backend uses
 * ISO megabytes.
 */
export const VIDEO_MAX_SIZE = 1000 * 1000 * 100 // 100mb

export const SUPPORTED_MIME_TYPES = [
  'video/mp4',
  'video/mpeg',
  'video/webm',
  'video/quicktime',
  'image/gif',
] as const

export type SupportedMimeTypes = (typeof SUPPORTED_MIME_TYPES)[number]

export const EMOJI_REACTION_LIMIT = 5

export const urls = {
  website: {
    blog: {
      findFriendsAnnouncement:
        'https://bsky.social/about/blog/12-16-2025-find-friends',
      initialVerificationAnnouncement: `https://bsky.social/about/blog/04-21-2025-verification`,
      searchTipsAndTricks: 'https://bsky.social/about/blog/05-31-2024-search',
    },
    support: {
      findFriendsPrivacyPolicy:
        'https://bsky.social/about/support/find-friends-privacy-policy',
    },
  },
}

export const PUBLIC_APPVIEW = 'https://appview.taderok.com'
export const PUBLIC_APPVIEW_DID = 'did:web:appview.taderok.com'
export const PUBLIC_STAGING_APPVIEW_DID = 'did:web:api.staging.bsky.dev'

export const DEV_ENV_APPVIEW = `http://localhost:2584` // always the same
export const DEV_ENV_APPVIEW_DID = `did:plc:dw4kbjf5mn7nhenabiqpkyh3` // always the same

// temp hack for e2e - esb
export const BLUESKY_PROXY_HEADER = {
  value: `${BLUESKY_PROXY_DID}#bsky_appview`,
  get() {
    return this.value as ProxyHeaderValue
  },
  set(value: string) {
    this.value = value
  },
}

export const CHAT_SERVICE_URL: string =
  process.env.EXPO_PUBLIC_CHAT_SERVICE_URL || 'https://chat.taderok.com'

export const DM_SERVICE_HEADERS = {
  'atproto-proxy': `${CHAT_PROXY_DID}#bsky_chat`,
}

export const BLUESKY_MOD_SERVICE_HEADERS = {
  'atproto-proxy': `${BSKY_LABELER_DID}#atproto_labeler`,
}

export const BLUESKY_NOTIF_SERVICE_HEADERS = {
  'atproto-proxy': `${BLUESKY_PROXY_DID}#bsky_notif`,
}

export const webLinks = {
  tos: `https://bsky.social/about/support/tos`,
  privacy: `https://bsky.social/about/support/privacy-policy`,
  community: `https://bsky.social/about/support/community-guidelines`,
  communityDeprecated: `https://bsky.social/about/support/community-guidelines-deprecated`,
}

// Video autoplay
export const AUTOPLAY_ENABLED = false
